import React, { useEffect, useState } from "react"
import { Badge, Button, Card, CardBody, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap"
import {
  activateRenewalReminderTemplate,
  createRenewalReminderTemplate,
  deleteRenewalReminderTemplate,
  getRenewalReminderTemplates,
  updateRenewalReminderSupportNumber,
  updateRenewalReminderTemplate,
} from "helpers/backendHelpers/renewalReminders"

const VARIABLES = ["{{Customer Name}}", "{{Expiry Date}}", "{{Plan Name}}", "{{Amount}}", "{{Duration}}", "{{Support Number}}"]
const EMPTY_TEMPLATE = { name: "", message: "" }

const getTokenDeletionRange = ({ value, selectionStart, selectionEnd, key }) => {
  for (const token of VARIABLES) {
    let tokenStart = value.indexOf(token)
    while (tokenStart !== -1) {
      const tokenEnd = tokenStart + token.length
      const cursorDeletesToken = selectionStart === selectionEnd && (
        (key === "Backspace" && selectionStart > tokenStart && selectionStart <= tokenEnd) ||
        (key === "Delete" && selectionStart >= tokenStart && selectionStart < tokenEnd)
      )
      // Keep normal browser behaviour for multi-line selections and Ctrl+A.
      // Atomic deletion applies only to one cursor inside a placeholder.
      if (cursorDeletesToken) return { start: tokenStart, end: tokenEnd }
      tokenStart = value.indexOf(token, tokenEnd)
    }
  }
  return null
}

const RenewalReminderTemplateCard = () => {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activatingId, setActivatingId] = useState(null)
  const [deletingId, setDeletingId] = useState(null)
  const [modal, setModal] = useState({ open: false, template: null })
  const [previewTemplate, setPreviewTemplate] = useState(null)
  const [form, setForm] = useState(EMPTY_TEMPLATE)
  const [commonSupportNumber, setCommonSupportNumber] = useState("")
  const [savingSupportNumber, setSavingSupportNumber] = useState(false)

  const loadTemplates = async () => {
    try {
      const response = await getRenewalReminderTemplates()
      const data = response?.payload?.data || []
      setTemplates(data)
      setCommonSupportNumber(data.find(template => template.isDefault)?.supportNumber || "")
    } catch (_) {
      setTemplates([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTemplates()
  }, [])

  const openCreate = () => {
    setForm(EMPTY_TEMPLATE)
    setModal({ open: true, template: null })
  }

  const openEdit = template => {
    setForm({ name: template.name, message: template.message })
    setModal({ open: true, template })
  }

  const closeModal = () => {
    if (!saving) setModal({ open: false, template: null })
  }

  const addVariable = token => {
    setForm(current => ({ ...current, message: `${current.message}${current.message ? " " : ""}${token}` }))
  }

  const handleMessageKeyDown = event => {
    if (event.key !== "Backspace" && event.key !== "Delete") return
    const range = getTokenDeletionRange({
      value: event.currentTarget.value,
      selectionStart: event.currentTarget.selectionStart,
      selectionEnd: event.currentTarget.selectionEnd,
      key: event.key,
    })
    if (!range) return

    event.preventDefault()
    const input = event.currentTarget
    const nextMessage = `${input.value.slice(0, range.start)}${input.value.slice(range.end)}`
    setForm(current => ({ ...current, message: nextMessage }))
    window.requestAnimationFrame(() => input.setSelectionRange(range.start, range.start))
  }

  const saveTemplate = async () => {
    setSaving(true)
    try {
      if (modal.template) await updateRenewalReminderTemplate(modal.template._id, form)
      else await createRenewalReminderTemplate(form)
      setModal({ open: false, template: null })
      await loadTemplates()
    } catch (_) {
      // Shared API helper shows the error.
    } finally {
      setSaving(false)
    }
  }

  const saveSupportNumber = async () => {
    setSavingSupportNumber(true)
    try {
      await updateRenewalReminderSupportNumber(commonSupportNumber.trim())
      await loadTemplates()
    } catch (_) {
      // Shared API helper shows the error.
    } finally {
      setSavingSupportNumber(false)
    }
  }

  const activateTemplate = async id => {
    setActivatingId(id)
    try {
      await activateRenewalReminderTemplate(id)
      await loadTemplates()
    } catch (_) {
      // Shared API helper shows the error.
    } finally {
      setActivatingId(null)
    }
  }

  const deleteTemplate = async template => {
    if (!window.confirm(`Delete template "${template.name}"?`)) return
    setDeletingId(template._id)
    try {
      await deleteRenewalReminderTemplate(template._id)
      await loadTemplates()
    } catch (_) {
      // Shared API helper shows the error.
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <Card>
      <CardBody>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-end gap-2 mb-3">
          <div>
            <Label className="fw-bold mb-1"><i className="bx bx-message-square-dots text-primary me-2" />Renewal Reminder Templates</Label>
            <p className="text-muted small mb-0">Create multiple messages and select exactly one active template for renewal reminders.</p>
          </div>
          <div className="d-flex flex-column flex-md-row align-items-md-end gap-2">
            <div>
              <Label className="small mb-1">Common Support Number</Label>
              <Input value={commonSupportNumber} maxLength="30" placeholder="e.g. +91 98765 43210" onChange={event => setCommonSupportNumber(event.target.value)} />
            </div>
            <Button color="secondary" disabled={savingSupportNumber || !commonSupportNumber.trim()} onClick={saveSupportNumber}>
              {savingSupportNumber ? <Spinner size="sm" /> : "Save Number"}
            </Button>
            <Button color="primary" onClick={openCreate}><i className="bx bx-plus me-1" />Add Template</Button>
          </div>
        </div>
        {loading ? (
          <div className="text-center py-4"><Spinner color="primary" /></div>
        ) : templates.length ? (
          <div className="d-flex flex-column gap-2">
            {templates.map(template => (
              <div
                key={template._id}
                role="button"
                tabIndex={0}
                className={`border rounded p-3 d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 ${template.isActive ? "border-primary bg-light" : ""}`}
                style={{ cursor: template.isActive || activatingId ? "default" : "pointer" }}
                onClick={() => !template.isActive && !activatingId && activateTemplate(template._id)}
                onKeyDown={event => {
                  if (!template.isActive && !activatingId && (event.key === "Enter" || event.key === " ")) activateTemplate(template._id)
                }}
              >
                <div className="d-flex align-items-center gap-2">
                  <i className={`bx ${template.isActive ? "bx-check-circle text-primary" : activatingId === template._id ? "bx-loader-circle bx-spin text-primary" : "bx-circle text-muted"}`} style={{ fontSize: 21 }} />
                  <span className="fw-medium">{template.name}</span>
                  {template.isDefault && <Badge color="primary">Default</Badge>}
                  {template.isActive && <Badge color="success">Active</Badge>}
                </div>
                <div className="d-flex gap-2">
                  {template.isDefault && <Button color="light" title="View default template" aria-label="View default template" className="d-inline-flex align-items-center justify-content-center" style={{ width: 40, height: 34 }} onClick={event => { event.stopPropagation(); setPreviewTemplate(template) }}><i className="bx bx-show" style={{ fontSize: 22 }} /></Button>}
                  {!template.isDefault && <Button size="sm" color="light" onClick={event => { event.stopPropagation(); openEdit(template) }}><i className="bx bx-edit-alt me-1" />Edit</Button>}
                  {!template.isDefault && (
                    <Button size="sm" color="danger" outline disabled={deletingId === template._id} onClick={event => { event.stopPropagation(); deleteTemplate(template)} }>
                      {deletingId === template._id ? <Spinner size="sm" /> : <><i className="bx bx-trash me-1" />Delete</>}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : <div className="text-muted">No template available.</div>}
      </CardBody>

      <Modal isOpen={modal.open} toggle={closeModal} centered size="lg">
        <ModalHeader toggle={closeModal}>{modal.template ? "Edit Renewal Template" : "Add Renewal Template"}</ModalHeader>
        <ModalBody>
          <Label>Template Name</Label>
          <Input className="mb-3" value={form.name} maxLength="100" onChange={event => setForm(current => ({ ...current, name: event.target.value }))} />
          <Label>Message</Label>
          <Input type="textarea" rows="11" value={form.message} onKeyDown={handleMessageKeyDown} onChange={event => setForm(current => ({ ...current, message: event.target.value }))} />
          <div className="d-flex flex-wrap gap-2 mt-3">
            {VARIABLES.map(token => <Button key={token} type="button" size="sm" color="info" outline onClick={() => addVariable(token)}>{token}</Button>)}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" disabled={saving} onClick={closeModal}>Cancel</Button>
          <Button color="primary" disabled={saving || !form.name.trim() || !form.message.trim()} onClick={saveTemplate}>
            {saving ? <Spinner size="sm" /> : "Save"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={Boolean(previewTemplate)} toggle={() => setPreviewTemplate(null)} centered size="lg">
        <ModalHeader toggle={() => setPreviewTemplate(null)}>Default Renewal Template</ModalHeader>
        <ModalBody>
          <Label>Template Name</Label>
          <Input value={previewTemplate?.name || ""} readOnly className="mb-3" />
          <Label>Support Number</Label>
          <Input value={previewTemplate?.supportNumber || ""} readOnly className="mb-3" />
          <Label>Message</Label>
          <Input type="textarea" rows="11" value={previewTemplate?.message || ""} readOnly />
        </ModalBody>
        <ModalFooter><Button color="secondary" onClick={() => setPreviewTemplate(null)}>Close</Button></ModalFooter>
      </Modal>
    </Card>
  )
}

export default RenewalReminderTemplateCard
